import React, { useState } from "react";
import { toast } from "react-toastify";
import Pagination from "../../../public/Pagination";
import { Spinner } from "../../../atoms/Spinner";
import { CustomButton } from "../../../atoms/CustomButton";
import { DataTable } from "../../../atoms/DataTable";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { EmptyState } from "../../../atoms/EmptyState";
import MessageContentDrawer from "./MessageContentDrawer";
import ConfirmModal from "../../../public/ConfirmModal";
import {
  useKmmQueueSummary,
  useKmmQueueMessages,
  useKmmMessageDetail,
  useDeleteQueueMessage,
  useRetryQueueMessage,
} from "../../../../hooks/providers";

const STATUSES = ["NEW", "SENDING", "SENDED", "ERROR"];
const TYPES = [
  "CLIENT_MESSAGE",
  "CLIENT_SMS",
  "CLIENT_WHATSAPP",
  "ERROR_MESSAGE",
];
const PAGE_SIZE = 20;

const statusTone = (status: string) =>
  ((
    {
      NEW: "warning",
      SENDING: "info",
      SENDED: "success",
      ERROR: "failure",
    } as Record<string, any>
  )[status] || "gray");

const pct = (num: number, denom: number) =>
  denom > 0 ? `${((num / denom) * 100).toFixed(0)}%` : "0%";

const formatAge = (seconds?: number) => {
  if (seconds === undefined || seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const StatusCard: React.FC<{
  label: string;
  count: number;
  total: number;
}> = ({ label, count, total }) => (
  <div className="rounded-lg border border-gray-200 p-3">
    <span className="block text-xs uppercase text-gray-500">{label}</span>
    <span className="text-xl font-semibold text-gray-800">
      {pct(count, total)}
    </span>
    <span className="ml-1 text-xs text-gray-400">({count})</span>
  </div>
);

const KmmQueuePanel: React.FC<{ customer: string; entity: string }> = ({
  customer,
  entity,
}) => {
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [scheduledOnly, setScheduledOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const summary = useKmmQueueSummary(customer, entity);
  const params: Record<string, any> = { limit: PAGE_SIZE, offset };
  if (status) params.status = status;
  if (type) params.type = type;
  if (scheduledOnly) params.scheduled = true;
  const list = useKmmQueueMessages(customer, entity, params);
  const detail = useKmmMessageDetail(
    customer,
    entity,
    selectedId,
    !!selectedId
  );

  const { mutate: cancel, isLoading: isCancelling } = useDeleteQueueMessage();
  const { mutate: retry, isLoading: isRetrying } = useRetryQueueMessage();
  const busy = isCancelling || isRetrying;
  const [pendingCancel, setPendingCancel] = useState<any | null>(null);

  const resetPage = () => setOffset(0);

  const act = (
    fn: any,
    message_id: string,
    okMsg: string,
    after?: () => void
  ) => {
    fn(
      {
        customer_identification: customer,
        entity_identification: entity,
        message_id,
      },
      {
        onSuccess: () => {
          toast.success(okMsg);
          after?.();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || err.message);
          after?.();
        },
      }
    );
  };

  const byStatus = summary.data?.by_status || {};
  const total = STATUSES.reduce((sum, s) => sum + (byStatus[s] || 0), 0);
  const messages: any[] = list.data?.messages ?? [];
  const listTotal: number = list.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">
          Queue summary
        </h3>
        {summary.isLoading ? (
          <Spinner size="sm" />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatusCard
              label="Pending"
              count={byStatus.NEW ?? 0}
              total={total}
            />
            <StatusCard
              label="In flight"
              count={byStatus.SENDING ?? 0}
              total={total}
            />
            <StatusCard
              label="Sent"
              count={byStatus.SENDED ?? 0}
              total={total}
            />
            <StatusCard
              label="Failed"
              count={byStatus.ERROR ?? 0}
              total={total}
            />
            <div className="rounded-lg border border-gray-200 p-3">
              <span className="block text-xs uppercase text-gray-500">
                Scheduled
              </span>
              <span className="text-xl font-semibold text-gray-800">
                {summary.data?.scheduled_count ?? 0}
              </span>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <span className="block text-xs uppercase text-gray-500">
                Oldest pending
              </span>
              <span className="text-xl font-semibold text-gray-800">
                {formatAge(summary.data?.oldest_pending_age_seconds)}
              </span>
            </div>
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="mr-auto text-sm font-semibold text-gray-900">
            Messages
          </h3>
          <select
            className="rounded-md border border-gray-300 p-1.5 text-sm"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              resetPage();
            }}
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-gray-300 p-1.5 text-sm"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              resetPage();
            }}
          >
            <option value="">All types</option>
            {TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {tp}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={scheduledOnly}
              onChange={(e) => {
                setScheduledOnly(e.target.checked);
                resetPage();
              }}
            />
            Scheduled only
          </label>
        </div>

        {list.isLoading ? (
          <Spinner size="sm" />
        ) : messages.length === 0 ? (
          <EmptyState title="No message for this filter" bordered />
        ) : (
          <>
            <DataTable>
              <DataTable.Head>
                <DataTable.HeadCell>Type</DataTable.HeadCell>
                <DataTable.HeadCell>Status</DataTable.HeadCell>
                <DataTable.HeadCell>Priority</DataTable.HeadCell>
                <DataTable.HeadCell>To</DataTable.HeadCell>
                <DataTable.HeadCell>Planned</DataTable.HeadCell>
                <DataTable.HeadCell>Inserted</DataTable.HeadCell>
                <DataTable.HeadCell className="text-right">
                  Actions
                </DataTable.HeadCell>
              </DataTable.Head>
              <DataTable.Body>
                {messages.map((m) => (
                  <DataTable.Row
                    key={m.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedId(m.id)}
                  >
                    <DataTable.Cell>{m.type}</DataTable.Cell>
                    <DataTable.Cell>
                      <StatusBadge color={statusTone(m.status)} size="xs">
                        {m.status}
                      </StatusBadge>
                    </DataTable.Cell>
                    <DataTable.Cell className="text-gray-600">
                      {m.priority}
                    </DataTable.Cell>
                    <DataTable.Cell>{m.to}</DataTable.Cell>
                    <DataTable.Cell className="text-gray-600">
                      {m.planned_date
                        ? new Date(m.planned_date).toLocaleString()
                        : "—"}
                    </DataTable.Cell>
                    <DataTable.Cell className="text-gray-600">
                      {m.insertion_date
                        ? new Date(m.insertion_date).toLocaleString()
                        : "—"}
                    </DataTable.Cell>
                    <DataTable.Cell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        {m.status === "ERROR" && (
                          <CustomButton
                            variant="secondary"
                            size="xs"
                            onClick={() =>
                              act(retry, m.id, "Message re-queued.")
                            }
                            disabled={busy}
                            title="Retry"
                          />
                        )}
                        {m.status !== "SENDED" && (
                          <CustomButton
                            variant="secondary"
                            size="xs"
                            onClick={() => setPendingCancel(m)}
                            disabled={busy}
                            title="Cancel"
                          />
                        )}
                      </div>
                    </DataTable.Cell>
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable>

            <div className="mt-2 flex items-center justify-end gap-3">
              <span className="text-xs tabular-nums text-gray-500">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, listTotal)} of{" "}
                {listTotal}
              </span>
              <Pagination
                pageCount={Math.ceil(listTotal / PAGE_SIZE)}
                currentPage={Math.floor(offset / PAGE_SIZE)}
                onPageChange={(page) => setOffset(page * PAGE_SIZE)}
              />
            </div>
          </>
        )}
      </section>

      <MessageContentDrawer
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title="Message content"
        isLoading={detail.isLoading}
        data={detail.data}
      />

      <ConfirmModal
        show={!!pendingCancel}
        onClose={() => setPendingCancel(null)}
        title="Cancel this message?"
        description={
          <span>
            It leaves the queue and is never sent. This cannot be undone, and
            the recipient is never told.
            <br />
            <strong>{pendingCancel?.type}</strong> to{" "}
            <strong>{pendingCancel?.to || "an unknown recipient"}</strong>,
            currently <strong>{pendingCancel?.status}</strong>.
          </span>
        }
        cancelBtnName="Keep it"
        cancelBtnFn={() => setPendingCancel(null)}
        confirmBtnName="Cancel sending"
        confirmBtnFn={() =>
          act(cancel, pendingCancel.id, "Message cancelled.", () =>
            setPendingCancel(null)
          )
        }
        isLoading={isCancelling}
        ConfirmBtnColor="red"
      />
    </div>
  );
};

export default KmmQueuePanel;
